import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Controller, useForm, SubmitHandler } from "react-hook-form";
import toast from "react-hot-toast";
import Loader from "../../../components/loader";
import Input from "../../../components/elements/form/input";
import Button from "../../../components/elements/button";
import SelectBox from "../../../components/elements/form/selectBox";
import { useUserApi } from "../../../store/api/hooks/userApiHook";
import { useFacilitiesApi } from "../../../store/api/hooks/facilitiesApiHook";
import { getSelectedOptions } from "../../../_common/functions";

// Types
interface CreateUserFormProps {
    UserID?: number | null;
    onCreate?: () => void;
    onCancel?: () => void;
}

interface SelectOption {
    label: string;
    value: number;
    data?: any;
}

interface FormValues {
    firstName: string;
    lastName: string;
    emailID: string;
    phone: string;
    userTypeIDs: SelectOption | null;
    facilityIDs: SelectOption[];
    isPushNotificationEnabled: boolean;
}

interface UserData {
    userID: number;
    firstName: string;
    lastName: string;
    emailID: string;
    phone: string;
    userCode?: string;
    userTypes?: Array<{ userTypeID: number }>;
    facilities?: Array<{ facilityID: number }>;
    isPushNotificationEnabled?: boolean;
}

// Constants
const FORM_DEFAULT_VALUES: FormValues = {
    firstName: "",
    lastName: "",
    emailID: "",
    phone: "",
    userTypeIDs: null,
    facilityIDs: [],
    isPushNotificationEnabled: false,
};

const VALIDATION_RULES = {
    firstName: { required: "First Name is required" },
    lastName: { required: "Last Name is required" },
    emailID: { required: "Email is required" },
    phone: {
        pattern: {
            value: /^[0-9]{10}$/,
            message: "Enter a valid 10-digit US phone number",
        },
    },
    userTypeIDs: { required: "User Type is required" },
};

const CreateUser: React.FC<CreateUserFormProps> = ({
    UserID = null,
    onCreate = () => { },
    onCancel = () => { },
}) => {
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<FormValues>({
        defaultValues: FORM_DEFAULT_VALUES,
    });

    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [userTypeOptions, setUserTypeOptions] = useState<SelectOption[]>([]);
    const [facilityOptions, setFacilityOptions] = useState<SelectOption[]>([]);
    const [editData, setEditData] = useState<UserData | null>(null);
    const fetchedUserRef = useRef<number | null>(null); // Track which user has been fetched
    const isInitializedRef = useRef<boolean>(false); // Track if initial data is loaded

    const userApi = useUserApi();
    const facilityApi = useFacilitiesApi();

    // Memoized API functions
    const getUserTypes = useCallback(() => {
        userApi.getUserTypes(
            {},
            (resp: any) => {
                const userTypeList = resp.userTypeList?.map((data: any) => ({
                    label: data.name,
                    value: data.userTypeID,
                    data,
                })) || [];
                setUserTypeOptions(userTypeList);
            },
            (error: any) => {
                toast.error(error);
            }
        );
    }, [userApi]);

    const getFacilityList = useCallback(() => {
        facilityApi.getFacilityList(
            {},
            (resp: any) => {
                const facilityList = resp.facilityList?.map((f: any) => ({
                    label: f.facilityName,
                    value: f.facilityID,
                    data: f,
                })) || [];
                setFacilityOptions(facilityList);
            },
            (error: any) => {
                console.error("Facility fetch error:", error);
                toast.error(error);
            }
        );
    }, [facilityApi]);

    // Function to fetch user data for editing
    const getUserData = useCallback((userID: number) => {
        // Prevent duplicate API calls for the same user
        if (fetchedUserRef.current === userID) {
            return;
        }

        setShowLoader(true);
        fetchedUserRef.current = userID;

        userApi.getUser(
            { id: userID },
            (resp: any) => {
                if (resp?.successful && resp?.userList?.length > 0) {
                    setEditData(resp.userList[0]);
                }
                setShowLoader(false);
            },
            (error: any) => {
                console.error("User fetch error:", error);
                toast.error(error);
                setShowLoader(false);
                fetchedUserRef.current = null; // Reset on error
            }
        );
    }, [userApi]);

    const onSubmit: SubmitHandler<FormValues> = async (data) => {        
        setShowLoader(true);

        const userPayload: any = {
            firstName: data.firstName,
            lastName: data.lastName,
            emailID: data.emailID,
            phone: data.phone,
            isPushNotificationEnabled: data.isPushNotificationEnabled,
            userTypeIDs: data.userTypeIDs
                ? [
                    {
                        userTypeID: Number(data.userTypeIDs.value),
                    },
                ]
                : [],
            facilityIDs: data.facilityIDs && typeof data.facilityIDs !=='undefined' && Array.isArray(data.facilityIDs) ? data.facilityIDs.filter((f: any) => f).map(f => ({
                facilityID: Number(f.value),
            })) : [],
        };

        if (UserID) {
            userPayload.userID = UserID; // add only if user id is present
        }

        const payload = {
            objRequestModel: [userPayload],
        };

        try {
            const response: any = await new Promise((resolve, reject) => {
                userApi.saveUser(payload, resolve, reject);
            });

            if (response?.successful) {
                toast.success(response?.message || `User has been ${UserID ? 'updated' : 'created'} successfully`);
                onCreate();
            } else {
                toast.error(response?.message || "Failed to save user, please try again");
            }
        } catch (error) {
            toast.error("Network error, unable to save user");
        } finally {
            setShowLoader(false);
        }
    }

    // Initialize data on component mount
    useEffect(() => {
        if (!isInitializedRef.current) {
            getUserTypes();
            getFacilityList();
            isInitializedRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Separate effect for UserID changes
    useEffect(() => {
        if (UserID && UserID !== fetchedUserRef.current) {
            getUserData(UserID);
        } else if (!UserID) {
            setEditData(null);
            fetchedUserRef.current = null;
        }
    }, [UserID, getUserData]);

    // Reset form when editData is null
    useEffect(() => {
        if (editData == null) {
            reset(FORM_DEFAULT_VALUES);
        }
    }, [editData, reset]);

    // Prefill form when editData is available
    useEffect(() => {
        if (editData) {
            const selectedUserType = editData.userTypes?.length
                ? userTypeOptions.find(option => option.value === editData.userTypes![0].userTypeID) || null
                : null;

            reset({
                firstName: editData.firstName || "",
                lastName: editData.lastName || "",
                emailID: editData.emailID || "",
                phone: editData.phone || "",
                userTypeIDs: selectedUserType,
                facilityIDs: getSelectedOptions(
                    editData.facilities?.map((f: any) => f.facilityID) || [],
                    facilityOptions
                ),
                isPushNotificationEnabled: editData.isPushNotificationEnabled || false,
            });
        }
    }, [editData, userTypeOptions, facilityOptions, reset]);


    // Cleanup effect
    useEffect(() => {
        return () => {
            fetchedUserRef.current = null;
            isInitializedRef.current = false;
        };
    }, []);

    // Memoized values
    const isEditMode = useMemo(() => Boolean(UserID), [UserID]);
    const submitButtonText = useMemo(() => isEditMode ? 'Update' : 'Save', [isEditMode]);

    return (
        <div className="relative flex flex-col">
            {showLoader && (
                <div className="absolute p-8 w-full h-full z-[2] bg-white bg-opacity-30">
                    <Loader />
                </div>
            )}

            <form
                onSubmit={handleSubmit(onSubmit)}
                className={`${showLoader ? "hidden" : "flex flex-col flex-1"}`}
                noValidate
            >
                {/* form body */}
                <div className="p-4">
                    {/* {editData && editData?.userID && (
                        <div className="flex bg-green-50 py-2 px-3 mb-4">
                            <h6 className="text-[0.813rem] font-semibold">User Code</h6>
                            <p className="text-[0.813rem] font-normal pl-3">{editData?.userCode || editData?.userID}</p>
                        </div>
                    )} */}

                    <div className="grid grid-cols-2 gap-4">
                        <Controller
                            name="firstName"
                            control={control}
                            rules={VALIDATION_RULES.firstName}
                            render={({ field }) => (
                                <Input
                                    label="First Name"
                                    type="text"
                                    {...field}
                                    required
                                    error={errors.firstName?.message as any}
                                    placeholder="Enter First Name"
                                />
                            )}
                        />
                        <Controller
                            name="lastName"
                            control={control}
                            rules={VALIDATION_RULES.lastName}
                            render={({ field }) => (
                                <Input
                                    label="Last Name"
                                    type="text"
                                    {...field}
                                    required
                                    error={errors.lastName?.message as any}
                                    placeholder="Enter Last Name"
                                />
                            )}
                        />
                        <Controller
                            name="emailID"
                            control={control}
                            rules={VALIDATION_RULES.emailID}
                            render={({ field }) => (
                                <Input
                                    label="Email"
                                    type="email"
                                    {...field}
                                    required
                                    error={errors.emailID?.message as any}
                                    placeholder="Enter Email"
                                />
                            )}
                        />
                        <Controller
                            name="phone"
                            control={control}
                            rules={VALIDATION_RULES.phone}
                            render={({ field: { value, onChange } }) => (
                                <Input
                                    label="Phone"
                                    type="text"
                                    value={value}
                                    onChange={(e: any) => {
                                        const onlyNums = e.target.value.replace(/\D/g, "");
                                        onChange(onlyNums);
                                    }}
                                    maxLength={10}
                                    placeholder="1XXXXXXXXX"
                                    error={errors.phone?.message as any}
                                />
                            )}
                        />
                    </div>

                    <Controller
                        name="userTypeIDs"
                        control={control}
                        rules={VALIDATION_RULES.userTypeIDs}
                        render={({ field: { value, onChange } }) => (
                            <SelectBox
                                label="Select User Type"
                                value={value}
                                onChange={onChange}
                                placeholder="Choose User Type"
                                options={userTypeOptions}
                                error={errors.userTypeIDs?.message as any}
                                required
                            />
                        )}
                    />

                    <Controller
                        name="facilityIDs"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <SelectBox
                                label="Select Facility"
                                value={value}
                                onChange={onChange}
                                isMulti={true}
                                placeholder="Choose Facility"
                                options={facilityOptions}
                                error={errors.facilityIDs?.message as any}
                            />
                        )}
                    />

                    <Controller
                        name="isPushNotificationEnabled"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <div className="flex items-center space-x-2 mt-2">
                                <input
                                    type="checkbox"
                                    id="pushNotification"
                                    checked={value}
                                    onChange={(e) => onChange(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                                />
                                <label
                                    htmlFor="pushNotification"
                                    className="text-sm font-medium text-gray-700"
                                >
                                    Receive Notification for selected Facilities
                                </label>
                            </div>
                        )}
                    />
                </div>

                {/* sticky footer */}
                <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-end gap-2">
                    <Button
                        type="button"
                        theme="secondary"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button type="submit">{submitButtonText}</Button>
                </div>
            </form>
        </div>
    );
};

export default CreateUser;